import { useRef, useState } from 'react';
import styles from './index.module.less';
import { getColor } from './colorConverter';
import classNames from 'classnames';
import type { Tools, Theme } from '../../../core/types';
import { Color, FontType, Size, Align } from '../../../core/enums';
import withEventTracking from '../../../utilities/withEventTracking';
import { Box } from '../../archived/box';
import { Icon } from '../../atoms/icon';
import { DismissibleContent } from '../../styleless/dismissible-content';
import { Typography } from '../../archived/typography';
import Element from '../../../core/Element';

interface ToolTipProps {
  icon?: {
    imageName: string;
  };
  customization?: CustomizationProps;
  data: {
    content?: string | React.ReactNode;
  };
  $direction?: 'top' | 'bottom' | 'right' | 'left';
  $maxWidth?: number;
  $minWidth?: number;
  qeId?: string;
  tools?: Tools;
  children?: React.ReactNode;
  ariaControlsId: string;
  disableFocus: boolean;
  onToggle: (isOpen: boolean) => void;
}

interface CustomizationProps {
  tooltipText: Theme;
  tooltip: Theme;
  dynamicAriaLabel?: string;
}

const defaultThemes = {
  tooltip: {
    bgColor: Color.BrightBlue,
    padding: 20,
    fontFamily: FontType.BentonRegular,
    color: Color.White,
    rounded: Size.MD,
    textAlign: Align.Left,
  },
};

const getArrowDirection = (direction: string) => {
  switch (direction) {
    case 'bottom':
      return styles.tooltipArrowBottom;
    case 'left':
      return styles.tooltipArrowLeft;
    case 'right':
      return styles.tooltipArrowRight;
    default:
      return styles.tooltipArrowTop;
  }
};

const ToolTip = ({
  qeId = 'tooltip',
  customization,
  icon = {
    imageName: 'dls-glyph-info',
  },
  $direction = 'top',
  $maxWidth = 280,
  $minWidth = 130,
  data = { content: '' },
  ariaControlsId,
  disableFocus = true,
  onToggle = () => {},
}: ToolTipProps) => {
  const [isOpen, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>();

  const getArrowColor = (direction: string) => {
    switch (direction) {
      case 'bottom':
        return {
          borderBottomColor: getColor(
            customization?.tooltip.bgColor || Color.BrightBlue
          ),
        };
      case 'left':
        return {
          borderLeftColor: getColor(
            customization?.tooltip.bgColor || Color.BrightBlue
          ),
        };
      case 'right':
        return {
          borderRightColor: getColor(
            customization?.tooltip.bgColor || Color.BrightBlue
          ),
        };
      default:
        return {
          borderTopColor: getColor(
            customization?.tooltip.bgColor || Color.BrightBlue
          ),
        };
    }
  };

  const TrackedToolTipIcon = withEventTracking((props) => (
    <Element
      {...props}
      className={styles.tooltipbtn}
      type="button"
      qeId={`${isOpen ? 'close-' : 'open-'}tooltip-${qeId}`}
      style={{ margin: 2 }}
      Component="button"
      aria-describedby={`${qeId}_tooltip`}
      aria-controls={ariaControlsId}
      theme={{ focusDark: true }}
    >
      <Icon
        directory="dls"
        imageName={
          isOpen
            ? 'dls-icon-cancel-circle'
            : icon?.imageName ?? 'dls-glpyh-info'
        }
        $size={Size.SM}
        theme={{ color: Color.BrightBlue }}
      />
    </Element>
  ));

  const dynamicAriaLabel = customization?.dynamicAriaLabel;
  const ariaLabel = dynamicAriaLabel
    ? `${dynamicAriaLabel} ${isOpen ? 'Close' : 'Open'}`
    : `${isOpen ? 'Close' : 'Open'} Tooltip`;
  return (
    <div className={styles.root}>
      <TrackedToolTipIcon
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-controls={ariaControlsId}
        onClick={() => {
          setOpen(true);
          onToggle(true);
          setTimeout(() => disableFocus && buttonRef.current?.focus(), 0);
        }}
        tools={{
          tracking: {
            methods: ['click'],
            element: 'Tooltip',
            details: [isOpen ? 'closed' : 'opened', qeId || ''],
          },
        }}
        ref={buttonRef}
      />
      <Element
        className={classNames(styles.tooltipContent, {
          [styles.showTooltipContent]: isOpen,
        })}
        id={ariaControlsId}
      >
        <DismissibleContent
          isOpen={isOpen}
          toggle={() => {
            setOpen(false);
            onToggle(false);
            buttonRef?.current?.focus();
          }}
          disableFocus={disableFocus}
          ignoreElementRef={buttonRef.current}
        >
          <span
            className={`${styles.tooltipArrow} ${getArrowDirection(
              $direction
            )}`}
            style={getArrowColor($direction)}
          />
          <div
            className={classNames(styles.tooltipWrapper, {
              [styles[$direction]]: $direction,
            })}
            style={{
              maxWidth: `${$maxWidth}px`,
              minWidth: `${$minWidth}px`,
            }}
          >
            <Box
              className={styles.tooltip}
              theme={{
                ...defaultThemes.tooltip,
                ...customization?.tooltip,
              }}
              qeId={`tooltip-content-${qeId}`}
            >
              <Typography
                $family={FontType.BentonRegular}
                $size="14px"
                Component="div"
                theme={{
                  margin: 0,
                  ...customization?.tooltipText,
                }}
              >
                {data.content}
              </Typography>
            </Box>
          </div>
        </DismissibleContent>
      </Element>
    </div>
  );
};

export default withEventTracking<ToolTipProps>(ToolTip);
